import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';

/** What kind of in-app navigation was intercepted. `beforeunload` (browser
 * close / reload) is handled internally — the browser shows its own prompt and
 * there's no useful caller intent to surface. */
export type NavigationGuardIntent = {
  kind: 'link';
  href: string;
} | {
  kind: 'popstate';
};

/** Blocks in-app navigation while editing, giving the caller a chance to
 * intercept (typically: open a Save / Discard modal). The Next.js App Router
 * has no built-in leave-blocker, so this hook listens at the DOM level:
 *
 *  - capture-phase `<a>` click on `document` — intercepts in-app links only.
 *  - `popstate` on `window` — best effort; the URL has already changed.
 *  - `beforeunload` on `window` — `preventDefault()` to surface the browser's
 *    native prompt. No `onIntercept` call (no useful intent + the page is
 *    leaving anyway).
 *
 * The dirty state is read LIVE inside the handlers via the `isBlocked` getter,
 * so the caller's dirty flag can flip without re-running this effect (and
 * without re-attaching listeners on every keystroke).
 */
export function useNavigationGuard(opts) {
  const $ = _c(5);
  const {
    enabled,
    isBlocked,
    onIntercept
  } = opts;
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== isBlocked || $[2] !== onIntercept) {
    t0 = () => {
      if (!enabled) {
        return;
      }
      const onClick = event => {
        if (!isBlocked()) {
          return;
        }
        if (event.defaultPrevented) {
          return;
        }
        if (event.button !== 0) {
          return;
        }
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }
        const anchor = target.closest("a");
        if (!anchor) {
          return;
        }
        if (anchor.target && anchor.target !== "_self") {
          return;
        }
        if (anchor.hasAttribute("download")) {
          return;
        }
        const hrefAttr = anchor.getAttribute("href");
        if (!hrefAttr) {
          return;
        }
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin) {
          return;
        }
        if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash !== window.location.hash) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        onIntercept({
          kind: "link",
          href: url.pathname + url.search + url.hash
        });
      };
      const onPopstate = () => {
        if (!isBlocked()) {
          return;
        }
        onIntercept({
          kind: "popstate"
        });
      };
      const onBeforeUnload = event_0 => {
        if (!isBlocked()) {
          return;
        }
        event_0.preventDefault();
      };
      document.addEventListener("click", onClick, {
        capture: true
      });
      window.addEventListener("popstate", onPopstate);
      window.addEventListener("beforeunload", onBeforeUnload);
      return () => {
        document.removeEventListener("click", onClick, {
          capture: true
        });
        window.removeEventListener("popstate", onPopstate);
        window.removeEventListener("beforeunload", onBeforeUnload);
      };
    };
    t1 = [enabled, isBlocked, onIntercept];
    $[0] = enabled;
    $[1] = isBlocked;
    $[2] = onIntercept;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}